import React, { useEffect, useState } from "react";

import { dashboardService } from "api/services/dashboard.service";
import ContentHeader from "components/Layout/ContentHeader";

import type { DashboardStats, InstructorMonthlyStats, PracticeMonthlyStats } from "api/types/dashboard.types";

import "./MainHome.scss";

const FULFILLMENT_LABEL: Record<string, string> = {
  NEW: "신규 주문",
  PREPARING: "상품 준비중",
  IN_TRANSIT: "배송중",
  DELIVERED: "배송 완료",
  PURCHASE_CONFIRMED: "구매 확정",
};

const PAYMENT_LABEL: Record<string, string> = {
  CONFIRMED: "결제 완료",
  PENDING: "결제 대기",
  PAYMENT_ATTEMPTED: "결제 시도",
  PAYMENT_PROCESSING: "결제 처리중",
  CANCELLED: "취소",
  COMPLETED: "완료",
  PAYMENT_FAILED: "결제 실패",
};

function formatAmount(amount: number): string {
  return amount.toLocaleString("ko-KR") + "원";
}

function todayLabel(): string {
  return new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });
}

function prevMonth(year: number, month: number) {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

function nextMonth(year: number, month: number) {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

const MainHome: React.FC = () => {
  const now = new Date();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [orderLoading, setOrderLoading] = useState(true);
  const [orderError, setOrderError] = useState(false);

  const [practiceYear, setPracticeYear] = useState(now.getFullYear());
  const [practiceMonth, setPracticeMonth] = useState(now.getMonth() + 1);
  const [practiceStats, setPracticeStats] = useState<PracticeMonthlyStats | null>(null);
  const [practiceLoading, setPracticeLoading] = useState(true);
  const [practiceError, setPracticeError] = useState(false);

  const [instrYear, setInstrYear] = useState(now.getFullYear());
  const [instrMonth, setInstrMonth] = useState(now.getMonth() + 1);
  const [instrIdInput] = useState("");
  const [instrId, setInstrId] = useState<number | undefined>(undefined);
  const [_instrStats, setInstrStats] = useState<InstructorMonthlyStats | null>(null);
  const [_instrLoading, setInstrLoading] = useState(false);
  const [_instrError, setInstrError] = useState(false);

  useEffect(() => {
    dashboardService
      .getOrderStats()
      .then((res) => setStats(res.data))
      .catch(() => setOrderError(true))
      .finally(() => setOrderLoading(false));
  }, []);

  useEffect(() => {
    setPracticeLoading(true);
    setPracticeError(false);
    dashboardService
      .getPracticeMonthlyStats(practiceYear, practiceMonth)
      .then((res) => setPracticeStats(res.data))
      .catch(() => setPracticeError(true))
      .finally(() => setPracticeLoading(false));
  }, [practiceYear, practiceMonth]);

  function handlePrevMonth() {
    const p = prevMonth(practiceYear, practiceMonth);
    setPracticeYear(p.year);
    setPracticeMonth(p.month);
  }

  function handleNextMonth() {
    const n = nextMonth(practiceYear, practiceMonth);
    setPracticeYear(n.year);
    setPracticeMonth(n.month);
  }

  const isNextDisabled = practiceYear > now.getFullYear() || (practiceYear === now.getFullYear() && practiceMonth >= now.getMonth() + 1);

  useEffect(() => {
    if (instrId === undefined) return;
    setInstrLoading(true);
    setInstrError(false);
    setInstrStats(null);
    dashboardService
      .getInstructorMonthlyStats(instrYear, instrMonth, instrId)
      .then((res) => setInstrStats(res.data))
      .catch(() => setInstrError(true))
      .finally(() => setInstrLoading(false));
  }, [instrYear, instrMonth, instrId]);

  function _handleInstrPrevMonth() {
    const p = prevMonth(instrYear, instrMonth);
    setInstrYear(p.year);
    setInstrMonth(p.month);
  }

  function _handleInstrNextMonth() {
    const n = nextMonth(instrYear, instrMonth);
    setInstrYear(n.year);
    setInstrMonth(n.month);
  }

  function _handleInstrSearch() {
    const id = parseInt(instrIdInput, 10);
    if (!Number.isFinite(id) || id <= 0) return;
    setInstrId(id);
  }

  return (
    <div className="main-home">
      <ContentHeader title="대시보드" description={todayLabel()} />

      {/* ── 주문 통계 ── */}
      {orderLoading && <div className="main-home__loading">주문 통계를 불러오는 중...</div>}
      {orderError && <div className="main-home__error">주문 통계를 불러오지 못했습니다.</div>}

      {!orderLoading && !orderError && stats && (
        <>
          <section className="main-home__kpi">
            <div className="kpi-card">
              <span className="kpi-card__label">오늘 신규 주문</span>
              <span className="kpi-card__value">{stats.today.new_orders.toLocaleString()}</span>
              <span className="kpi-card__sub">건</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__label">오늘 매출</span>
              <span className="kpi-card__value kpi-card__value--amount">{formatAmount(stats.today.total_amount)}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__label">이번 주 주문</span>
              <span className="kpi-card__value">{stats.this_week.new_orders.toLocaleString()}</span>
              <span className="kpi-card__sub">건</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__label">이번 주 매출</span>
              <span className="kpi-card__value kpi-card__value--amount">{formatAmount(stats.this_week.total_amount)}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__label">이번 달 주문</span>
              <span className="kpi-card__value">{stats.this_month.new_orders.toLocaleString()}</span>
              <span className="kpi-card__sub">건</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__label">이번 달 매출</span>
              <span className="kpi-card__value kpi-card__value--amount">{formatAmount(stats.this_month.total_amount)}</span>
            </div>
            <div className="kpi-card kpi-card--total">
              <span className="kpi-card__label">전체 주문</span>
              <span className="kpi-card__value">{stats.total_orders.toLocaleString()}</span>
              <span className="kpi-card__sub">건</span>
            </div>
          </section>

          <section className="main-home__status-section">
            <div className="status-panel">
              <h2 className="status-panel__title">주문 처리 현황</h2>
              <ul className="status-panel__list">
                {Object.entries(stats.by_fulfillment_status).map(([key, count]) => (
                  <li key={key} className="status-panel__item">
                    <span className="status-panel__dot status-panel__dot--fulfillment" />
                    <span className="status-panel__name">{FULFILLMENT_LABEL[key] ?? key}</span>
                    <span className="status-panel__count">{count?.toLocaleString()}건</span>
                  </li>
                ))}
                {Object.keys(stats.by_fulfillment_status).length === 0 && <li className="status-panel__empty">데이터 없음</li>}
              </ul>
            </div>

            <div className="status-panel">
              <h2 className="status-panel__title">결제 상태 현황</h2>
              <ul className="status-panel__list">
                {Object.entries(stats.by_payment_status).map(([key, count]) => (
                  <li key={key} className="status-panel__item">
                    <span className="status-panel__dot status-panel__dot--payment" />
                    <span className="status-panel__name">{PAYMENT_LABEL[key] ?? key}</span>
                    <span className="status-panel__count">{count?.toLocaleString()}건</span>
                  </li>
                ))}
                {Object.keys(stats.by_payment_status).length === 0 && <li className="status-panel__empty">데이터 없음</li>}
              </ul>
            </div>
          </section>
        </>
      )}

      {/* ── 연습반 월별 통계 ── */}
      <section className="main-home__practice">
        <div className="practice-panel">
          <div className="practice-panel__header">
            <h2 className="practice-panel__title">연습반 월별 정산 현황</h2>
            <div className="practice-panel__nav">
              <button className="practice-panel__nav-btn" onClick={handlePrevMonth}>
                ‹
              </button>
              <span className="practice-panel__month">
                {practiceYear}년 {practiceMonth}월
              </span>
              <button className="practice-panel__nav-btn" onClick={handleNextMonth} disabled={isNextDisabled}>
                ›
              </button>
            </div>
          </div>

          {practiceLoading && <div className="main-home__loading">연습반 통계를 불러오는 중...</div>}
          {practiceError && <div className="main-home__error">연습반 통계를 불러오지 못했습니다.</div>}

          {!practiceLoading && !practiceError && practiceStats && (
            <>
              <div className="practice-panel__kpi">
                <div className="kpi-card">
                  <span className="kpi-card__label">총 세션 수</span>
                  <span className="kpi-card__value">{practiceStats.total_sessions.toLocaleString()}</span>
                  <span className="kpi-card__sub">회</span>
                </div>
                <div className="kpi-card">
                  <span className="kpi-card__label">총 수강생</span>
                  <span className="kpi-card__value">{practiceStats.total_students.toLocaleString()}</span>
                  <span className="kpi-card__sub">명</span>
                </div>
                <div className="kpi-card kpi-card--total">
                  <span className="kpi-card__label">총 정산 금액</span>
                  <span className="kpi-card__value kpi-card__value--amount">{formatAmount(practiceStats.total_amount)}</span>
                </div>
              </div>

              {practiceStats.by_pricing_type.length > 0 && (
                <table className="practice-panel__table">
                  <thead>
                    <tr>
                      <th>결제 유형</th>
                      <th>세션 수</th>
                      <th>수강생</th>
                      <th>정산 금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {practiceStats.by_pricing_type.map((row) => (
                      <tr key={row.pricing_type}>
                        <td>{row.pricing_type_display}</td>
                        <td>{row.sessions_count.toLocaleString()}회</td>
                        <td>{row.total_students.toLocaleString()}명</td>
                        <td>{formatAmount(row.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── 강사별 월별 통계 ── */}
      {/* <section className="main-home__practice">
        <div className="practice-panel">
          <div className="practice-panel__header">
            <h2 className="practice-panel__title">강사별 월별 정산 현황</h2>
            <div className="practice-panel__nav">
              <button className="practice-panel__nav-btn" onClick={handleInstrPrevMonth}>
                ‹
              </button>
              <span className="practice-panel__month">
                {instrYear}년 {instrMonth}월
              </span>
              <button
                className="practice-panel__nav-btn"
                onClick={handleInstrNextMonth}
                disabled={isInstrNextDisabled}
              >
                ›
              </button>
            </div>
          </div>

          <div className="practice-panel__search">
            <input
              type="number"
              className="practice-panel__search-input"
              placeholder="강사 ID 입력"
              value={instrIdInput}
              onChange={(e) => setInstrIdInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInstrSearch()}
              min={1}
            />
            <button className="practice-panel__search-btn" onClick={handleInstrSearch}>
              조회
            </button>
          </div>

          {instrId === undefined && (
            <div className="main-home__loading">강사 ID를 입력하고 조회하세요.</div>
          )}
          {instrLoading && <div className="main-home__loading">강사 통계를 불러오는 중...</div>}
          {instrError && <div className="main-home__error">강사 통계를 불러오지 못했습니다.</div>}

          {!instrLoading && !instrError && instrStats && (
            <>
              <p className="practice-panel__instructor-name">
                강사: <strong>{instrStats.instructor_name}</strong>
              </p>
              <div className="practice-panel__kpi">
                <div className="kpi-card">
                  <span className="kpi-card__label">총 세션 수</span>
                  <span className="kpi-card__value">
                    {instrStats.total_sessions.toLocaleString()}
                  </span>
                  <span className="kpi-card__sub">회</span>
                </div>
                <div className="kpi-card">
                  <span className="kpi-card__label">총 수강생</span>
                  <span className="kpi-card__value">
                    {instrStats.total_students.toLocaleString()}
                  </span>
                  <span className="kpi-card__sub">명</span>
                </div>
                <div className="kpi-card kpi-card--total">
                  <span className="kpi-card__label">총 정산 금액</span>
                  <span className="kpi-card__value kpi-card__value--amount">
                    {formatAmount(instrStats.total_amount)}
                  </span>
                </div>
              </div>

              {instrStats.by_lesson.length > 0 && (
                <table className="practice-panel__table">
                  <thead>
                    <tr>
                      <th>강습명</th>
                      <th>구분</th>
                      <th>세션 수</th>
                      <th>수강생</th>
                      <th>정산 금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {instrStats.by_lesson.map((row, i) => (
                      <tr key={i}>
                        <td>{row.lesson_name}</td>
                        <td>
                          {row.is_theory ? "이론" : row.day_order ? `Day ${row.day_order}` : "-"}
                        </td>
                        <td>{row.sessions_count.toLocaleString()}회</td>
                        <td>{row.total_students.toLocaleString()}명</td>
                        <td>{formatAmount(row.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {instrStats.by_lesson.length === 0 && (
                <p className="status-panel__empty">해당 월의 강습 기록이 없습니다.</p>
              )}
            </>
          )}
        </div>
      </section> */}
    </div>
  );
};

export default MainHome;
